import type { TFunction } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router';

import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_STATUSES,
  type FeedbackFilter,
  type FeedbackSort,
  useFeedbackModerators,
  useSuggestionList,
} from '@/entities/feedback';
import { useViewer } from '@/entities/viewer';
import { Button, Combobox, EmptyState, ErrorState, LoadingState } from '@/shared/ui';
import { isFeedbackStaff } from '@/entities/feedback';

import { filterFromParams, hasActiveFilters, paramsFromFilter } from './filter-params';
import { categoryLabel, statusLabel } from './labels';
import { SuggestionCard } from './SuggestionCard';
import styles from './FeedbackList.module.css';

const SEARCH_DELAY_MS = 300;

/** "My suggestions" for everybody, "All suggestions" for system moderators / administrators. */
export function FeedbackList() {
  const { t } = useTranslation();
  const viewer = useViewer();
  const user = viewer.status === 'authenticated' ? viewer.user : null;
  const staff = user ? isFeedbackStaff(user) : false;
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const filter = useMemo(() => filterFromParams(params, staff), [params, staff]);
  const query = useSuggestionList(filter);
  const moderators = useFeedbackModerators(staff && filter.scope === 'all');

  // The search box types freely; the address (and the request) follow after a short pause.
  const [typed, setTyped] = useState<string | null>(null);
  const text = typed ?? filter.q;
  const latest = useRef(filter);
  useEffect(() => {
    latest.current = filter;
  }, [filter]);
  useEffect(() => {
    if (typed === null) return;
    const timer = setTimeout(() => {
      apply(latest.current, true);
    }, SEARCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply only reads `filter`
  }, [typed]);

  /** Text still waiting for its pause goes along with any other change, so nothing is lost. */
  function apply(next: FeedbackFilter, replace = false, discardText = false) {
    setParams(paramsFromFilter({ ...next, q: discardText ? next.q : (typed ?? next.q) }), {
      replace,
    });
    setTyped(null);
  }

  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const filtered = hasActiveFilters(filter);
  const from = `${location.pathname}${location.search}`;
  const zone = user?.timezone;

  const statusOptions = [
    { value: 'any', label: t('feedback.filters.anyStatus') },
    ...FEEDBACK_STATUSES.map((s) => ({ value: s, label: statusLabel(t, s) })),
  ];
  const categoryOptions = [
    { value: 'any', label: t('feedback.filters.anyCategory') },
    ...FEEDBACK_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(t, c) })),
  ];
  const sortOptions: { value: FeedbackSort; label: string }[] = [
    { value: 'activity_desc', label: t('feedback.sort.activity_desc') },
    { value: 'activity_asc', label: t('feedback.sort.activity_asc') },
    { value: 'created_desc', label: t('feedback.sort.created_desc') },
    { value: 'created_asc', label: t('feedback.sort.created_asc') },
  ];
  const assigneeOptions = [
    { value: 'any', label: t('feedback.filters.anyAssignee') },
    { value: 'me', label: t('feedback.filters.assignedToMe') },
    { value: 'unassigned', label: t('feedback.filters.unassigned') },
    ...(moderators.data ?? []).map((m) => ({ value: m.id, label: m.display_name })),
  ];

  return (
    <div className={styles.wrap}>
      {staff && (
        <div className={styles.tabs} role="tablist" aria-label={t('feedback.tabs.label')}>
          {(['mine', 'all'] as const).map((scope) => (
            <button
              key={scope}
              type="button"
              role="tab"
              id={`feedback-tab-${scope}`}
              aria-selected={filter.scope === scope}
              aria-controls="feedback-results"
              tabIndex={filter.scope === scope ? 0 : -1}
              className={`${styles.tab} ${filter.scope === scope ? styles.active : ''}`}
              onClick={() => {
                apply({ ...filter, scope, assigned: '', attention: false });
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                  event.preventDefault();
                  apply({
                    ...filter,
                    scope: scope === 'mine' ? 'all' : 'mine',
                    assigned: '',
                    attention: false,
                  });
                }
              }}
            >
              {t(scope === 'mine' ? 'feedback.tabs.mine' : 'feedback.tabs.all')}
            </button>
          ))}
        </div>
      )}
      <search className={styles.tools}>
        <label className={styles.search}>
          <span className={styles.srOnly}>{t('feedback.filters.searchLabel')}</span>
          <input
            type="search"
            className={styles.searchInput}
            value={text}
            placeholder={t('feedback.filters.searchPlaceholder')}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
        </label>
        <div className={styles.row}>
          <Combobox
            size="sm"
            label={t('feedback.filters.status')}
            value={filter.status || 'any'}
            searchable={false}
            options={statusOptions}
            onChange={(value) => {
              apply({ ...filter, status: FEEDBACK_STATUSES.find((s) => s === value) ?? '' });
            }}
          />
          <Combobox
            size="sm"
            label={t('feedback.filters.category')}
            value={filter.category || 'any'}
            searchable={false}
            options={categoryOptions}
            onChange={(value) => {
              apply({ ...filter, category: FEEDBACK_CATEGORIES.find((c) => c === value) ?? '' });
            }}
          />
          <Combobox
            size="sm"
            label={t('feedback.filters.sort')}
            value={filter.sort}
            searchable={false}
            options={sortOptions}
            onChange={(value) => {
              apply({
                ...filter,
                sort: sortOptions.find((o) => o.value === value)?.value ?? 'activity_desc',
              });
            }}
          />
          {filter.scope === 'all' && (
            <Combobox
              size="sm"
              label={t('feedback.filters.assignee')}
              value={filter.assigned || 'any'}
              options={assigneeOptions}
              onChange={(value) => {
                apply({ ...filter, assigned: value === 'any' ? '' : value });
              }}
            />
          )}
        </div>
        <div className={styles.checks}>
          {filter.scope === 'all' && (
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={filter.attention}
                onChange={(event) => {
                  apply({ ...filter, attention: event.target.checked });
                }}
              />
              {t('feedback.filters.attention')}
            </label>
          )}
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={filter.unread}
              onChange={(event) => {
                apply({ ...filter, unread: event.target.checked });
              }}
            />
            {t(filter.scope === 'all' ? 'feedback.filters.unreadStaff' : 'feedback.filters.unread')}
          </label>
          {filtered && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                apply(
                  {
                    ...filter,
                    q: '',
                    status: '',
                    category: '',
                    assigned: '',
                    attention: false,
                    unread: false,
                  },
                  false,
                  true,
                );
              }}
            >
              {t('feedback.filters.reset')}
            </Button>
          )}
        </div>
      </search>
      <section
        id="feedback-results"
        role={staff ? 'tabpanel' : undefined}
        aria-labelledby={staff ? `feedback-tab-${filter.scope}` : undefined}
        aria-busy={query.isFetching}
        className={styles.results}
      >
        {query.isPending && <LoadingState />}
        {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
        {query.isSuccess && items.length === 0 && (
          <EmptyState title={emptyTitle(t, filter, filtered)}>
            {filter.scope === 'mine' && !filtered && (
              <Link className={styles.emptyAction} to="/app/feedback/new">
                {t('feedback.empty.mineAction')}
              </Link>
            )}
          </EmptyState>
        )}
        {items.length > 0 && (
          <ul
            className={styles.list}
            aria-label={t(filter.scope === 'all' ? 'feedback.tabs.all' : 'feedback.tabs.mine')}
          >
            {items.map((item) => (
              <SuggestionCard
                key={item.id}
                item={item}
                staff={filter.scope === 'all'}
                timeZone={zone}
                from={from}
              />
            ))}
          </ul>
        )}
        {query.hasNextPage && (
          <Button
            className={styles.more}
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {t('feedback.loadMore')}
          </Button>
        )}
      </section>
    </div>
  );
}

function emptyTitle(t: TFunction, filter: FeedbackFilter, filtered: boolean): string {
  if (filtered)
    return t(filter.unread && !filter.q ? 'feedback.empty.unread' : 'feedback.empty.search');
  return t(filter.scope === 'all' ? 'feedback.empty.all' : 'feedback.empty.mine');
}
